/**
 * La pagina partita a partita conclusa (V18): testata con i comandi
 * a icona (modifica, riapertura), e sotto le schede Info, Tabellino, Cronaca e
 * Voti nel componente Tabs, come nell'allenamento. Cambiando scheda cambia
 * solo il corpo; le schede si sostituiscono nella cronologia.
 *
 * Il comando Modifica, dopo la conferma, apre la stessa pagina in modifica
 * (`/modifica`): la testata dice che si modifica e porta indietro alla
 * partita, e le schede diventano Eventi (V25) e Carico, per chi può.
 *
 * Il tabellino lo legge la pagina, per la testata, e lo passa alle schede;
 * gli eventi e il carico lo fanno rileggere a ogni correzione.
 */

import { useCallback, useEffect, useState } from 'react';
import { Navigate, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { puo } from '@calcio/shared';
import { PencilIcon, TriangleAlert, Undo2Icon } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { chiama } from '../api/client.js';
import { useConferma } from '../comuni/Conferma.js';
import { Corpo } from '../comuni/Corpo.js';
import { messaggio, useDati } from '../comuni/dati.js';
import { AzioniTestata, MenuAzioni, PulsantePrincipale } from '../comuni/Azioni.js';
import { Testata } from '../comuni/Testata.js';
import type { CaricoPartitaLetto } from '../carichi/carichi.js';
import { useContesto } from '../navigazione/contesto.js';
import type { ContestoPartita } from './contesto.js';
import { rigaSopraPartita } from './partita.js';
import type { IntestazionePartita, RispostaTabellino } from './Tabellino.js';

type Scheda = 'info' | 'tabellino' | 'cronaca' | 'voti';
type SchedaModifica = 'eventi' | 'carico';

/** Scheda, coda del percorso ed etichetta, nell'ordine dei Tabs. */
const SCHEDE: readonly (readonly [Scheda, string, string])[] = [
  ['info', '', 'Info'],
  ['tabellino', '/tabellino', 'Tabellino'],
  ['cronaca', '/cronaca', 'Cronaca'],
  ['voti', '/voti', 'Voti'],
];
const SCHEDE_MODIFICA: readonly (readonly [SchedaModifica, string, string])[] = [
  ['eventi', '/modifica', 'Eventi'],
  ['carico', '/modifica/carico', 'Carico'],
];

export function PartitaConclusa({
  ss,
  partita,
  cronometrata,
  onRiaperta,
}: {
  ss: string;
  partita: IntestazionePartita;
  /** Avviata col timer almeno una volta: senza, la partita non si riapre. */
  cronometrata: boolean;
  onRiaperta: () => Promise<void>;
}) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { utente } = useContesto();
  const id = partita.id;
  const tab = useDati<RispostaTabellino>(`/partite/${id}/tabellino`);
  const [carico, setCarico] = useState<{ esiste: boolean; minutiDiversi: boolean } | null>(null);
  const [inCorso, setInCorso] = useState(false);
  const [chiedi, conferma] = useConferma();

  const opera = puo(utente.assegnazioni, 'timer.opera', ss);
  const leggeVoti = puo(utente.assegnazioni, 'voto.leggi', ss);
  const gestisce = puo(utente.assegnazioni, 'partita.gestisci', ss);
  const gestisceCarico = puo(utente.assegnazioni, 'allenamento.gestisci', ss);

  const leggiCarico = useCallback(async () => {
    if (!gestisceCarico) return;
    try {
      const c = await chiama<CaricoPartitaLetto>(`/partite/${id}/carico`);
      setCarico({ esiste: c.esiste, minutiDiversi: c.log.some((l) => l.minutiDiversi) });
    } catch {
      setCarico({ esiste: false, minutiDiversi: false });
    }
  }, [id, gestisceCarico]);

  useEffect(() => {
    void leggiCarico();
  }, [leggiCarico]);

  const base = `/sq/${ss}/partite/${id}`;
  const coda = pathname.slice(base.length).replace(/\/$/, '');
  const inModifica = coda === '/modifica' || coda.startsWith('/modifica/');
  const scheda = SCHEDE.find(([, c]) => c === coda)?.[0] ?? 'info';
  const visibili = SCHEDE.filter(([s]) => s !== 'voti' || leggeVoti);
  const schedaModifica = SCHEDE_MODIFICA.find(([, c]) => c === coda)?.[0] ?? 'eventi';
  const modificabili = SCHEDE_MODIFICA.filter(([s]) => (s === 'eventi' ? opera : gestisceCarico));

  // Eventi e carico si correggono in una pagina a parte, dopo la conferma.
  const modifica = async () => {
    const si = await chiedi({
      titolo: 'Vuoi modificare i dati della partita?',
      testo:
        'Eventi e carico si correggono in una pagina a parte. Ogni modifica agli eventi ricalcola minuti, partecipazioni e risultato.',
      azione: 'Modifica',
    });
    if (si) navigate(`${base}${modificabili[0]![1]}`);
  };

  const riapri = async () => {
    const si = await chiedi({
      titolo: 'Riaprire la partita?',
      testo: 'Si annulla il fischio finale e si torna al timer.',
      azione: 'Riapri',
    });
    if (!si) return;
    setInCorso(true);
    try {
      await chiama(`/partite/${id}/riapri`, { metodo: 'POST' });
      await onRiaperta();
      toast.success('Partita riaperta.');
    } catch (e) {
      toast.error(messaggio(e));
    } finally {
      setInCorso(false);
    }
  };

  const azioni = !inModifica && (modificabili.length > 0 || (gestisce && cronometrata)) && (
    <AzioniTestata>
      {modificabili.length > 0 && (
        <PulsantePrincipale
          etichetta="Modifica"
          Icona={PencilIcon}
          onClick={() => void modifica()}
        />
      )}
      <MenuAzioni
        voci={[
          gestisce &&
            cronometrata && {
              etichetta: 'Riapri la partita',
              Icona: Undo2Icon,
              disabled: inCorso,
              onSelect: () => void riapri(),
            },
        ]}
      />
    </AzioniTestata>
  );

  const contesto: ContestoPartita = {
    tabellino: tab.dati,
    errore: tab.errore,
    ricarica: async () => {
      await Promise.all([tab.ricarica(), leggiCarico()]);
    },
  };

  // Chi corregge solo il carico non ha la scheda Eventi: si parte dal carico.
  if (inModifica && schedaModifica === 'eventi' && !opera && gestisceCarico) {
    return <Navigate to={`${base}/modifica/carico`} replace />;
  }

  const sopra = rigaSopraPartita(partita);
  const schede = inModifica ? modificabili : visibili;
  const attiva = inModifica ? schedaModifica : scheda;

  return (
    <div className="pb-6">
      <Testata
        indietro={inModifica ? base : `/sq/${ss}`}
        sopra={inModifica ? `${partita.avversario} · ${sopra}` : sopra}
        titolo={inModifica ? 'Modifica partita' : partita.avversario}
        azione={azioni}
      >
        {carico?.minutiDiversi && (
          <div>
            <Badge className="bg-avviso text-avviso-foreground">
              <TriangleAlert />
              Carico con minuti da aggiornare
            </Badge>
          </div>
        )}
      </Testata>

      <Corpo className="flex flex-col gap-4">
        {schede.length > 1 && (
          <Tabs value={attiva}>
            <TabsList className="w-full justify-start overflow-x-auto [scrollbar-width:none]">
              {schede.map(([valore, c, etichetta]) => (
                <TabsTrigger key={valore} value={valore} asChild>
                  <NavLink to={`${base}${c}`} end replace>
                    {etichetta}
                  </NavLink>
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        )}
        <Outlet context={contesto} />
      </Corpo>
      {conferma}
    </div>
  );
}
